import React, { useState, useEffect, useMemo } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { getApiUrl } from "./config/api";
import AdminApp from "./admin/AdminApp";
import { UserLogin, UserSignup } from "./components/UserAuth";
import { mockData, customStyles } from "./data/mockData";
import MainLayout from "./layouts/MainLayout";
import MainSite from "./pages/MainSite";
import VideosPage from "./pages/VideosPage";
import ShortsPage from "./pages/ShortsPage";
import CategoryPage from "./pages/CategoryPage";
import SingleNewsPage from "./pages/SingleNewsPage";
import EpaperPage from "./pages/EpaperPage";

// --- Main App Component (Handles State and Routing) ---
export default function App() {
  const [lang, setLang] = useState("en");
  const [articles, setArticles] = useState([]);
  const [ticker, setTicker] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageVersion, setImageVersion] = useState(Date.now());

  const textData = mockData[lang];

  useEffect(() => {
    const fetchNews = async (isUpdate = false) => {
      try {
        if (!isUpdate) {
          setLoading(true);
        }
        const response = await fetch(getApiUrl("GET_NEWS"), {
          cache: "no-store",
        });
        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        if (data.success && Array.isArray(data.posts)) {
          setArticles(data.posts);
          setTicker(data.posts.slice(0, 5).map((p) => p.title));
          if (isUpdate) {
            setImageVersion(Date.now());
          }
        } else {
          throw new Error("Invalid data structure from API");
        }
      } catch (e) {
        if (!isUpdate) {
          setError(e.message);
        } else {
          console.error("Failed to auto-refresh articles:", e);
        }
      } finally {
        if (!isUpdate) {
          setLoading(false);
        }
      }
    };

    fetchNews();

    const channel = new BroadcastChannel("articles-update");
    channel.onmessage = () => {
      fetchNews(true);
    };

    return () => {
      channel.close();
    };
  }, []);

  const pdfArticles = useMemo(() => {
    return articles
      .filter((a) => a.pdf)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [articles]);

  if (loading)
    return (
      <div className="flex justify-center items-center h-screen text-xl">
        Loading News...
      </div>
    );
  if (error)
    return (
      <div className="flex justify-center items-center h-screen text-red-500 text-xl">
        Error: {error}
      </div>
    );

  return (
    <>
      <style>{customStyles}</style>
      <Router>
        <Routes>
          <Route
            element={
              <MainLayout
                lang={lang}
                setLang={setLang}
                textData={textData}
                ticker={ticker}
                pdfArticles={pdfArticles}
                imageVersion={imageVersion}
              />
            }
          >
            <Route
              path="/"
              element={
                <MainSite
                  lang={lang}
                  articles={articles}
                  textData={textData}
                  imageVersion={imageVersion}
                />
              }
            />
            <Route
              path="/videos"
              element={<VideosPage allArticles={articles} lang={lang} />}
            />
            <Route
              path="/shorts"
              element={<ShortsPage allArticles={articles} lang={lang} />}
            />
            <Route
              path="/category/:categoryName"
              element={
                <CategoryPage
                  allArticles={articles}
                  lang={lang}
                  textData={textData}
                  imageVersion={imageVersion}
                />
              }
            />
            <Route
              path="/news/:slug"
              element={
                <SingleNewsPage
                  allArticles={articles}
                  lang={lang}
                  textData={textData}
                  imageVersion={imageVersion}
                />
              }
            />
            <Route
              path="/epaper"
              element={<EpaperPage allArticles={articles} />}
            />
            <Route
              path="/epaper/:articleId"
              element={<EpaperPage allArticles={articles} />}
            />
          </Route>

          <Route path="/admin/*" element={<AdminApp />} />
          <Route path="/login" element={<UserLogin />} />
          <Route path="/signup" element={<UserSignup />} />
        </Routes>
      </Router>
    </>
  );
}
